import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Layout } from "../components/Navbar";
import { api } from "../api";
import { toast } from "sonner";
import { Labeled, MedicalField, BloodSelect } from "../components/BioFields";

const CLINICAL = ["conditions", "allergies", "medications", "surgeries", "family_history"];

export default function RegisterUser() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const ci = new URLSearchParams(window.location.search).get("ci");
  const fi = new URLSearchParams(window.location.search).get("fi");
  const [f, setF] = useState({
    email: "", password: "", nickname: "", first_name: "", last_name: "", birth_date: "",
    tax_id: "", phone: "", address_street: "", address_city: "", country: "IT", language: "it", timezone: "Europe/Rome",
    accept_terms: false,
    health_bio: {
      conditions: "", allergies: "", medications: "", surgeries: "", height: "", weight: "",
      blood_type: "", family_history: "", notes: "", field_status: {},
    },
  });
  useEffect(() => {
    if (!ci) return;
    api.get(`/clinical-invite/${ci}`).then(({ data }) => {
      setF((p) => ({ ...p, first_name: data.first_name || p.first_name, last_name: data.last_name || p.last_name,
        tax_id: data.tax_id || p.tax_id, email: data.email || p.email, birth_date: data.birth_date || p.birth_date }));
    }).catch(() => {});
  }, [ci]);
  const [errors, setErrors] = useState({});
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const clearErr = (k) => setErrors((e) => { if (!e[k]) return e; const n = { ...e }; delete n[k]; return n; });
  const setVal = (k, v) => { clearErr(k); setF((p) => ({ ...p, health_bio: { ...p.health_bio, [k]: v, field_status: { ...p.health_bio.field_status, [k]: undefined } } })); };
  const setStat = (k, st) => { if (st) clearErr(k); setF((p) => ({ ...p, health_bio: { ...p.health_bio, [k]: "", field_status: { ...p.health_bio.field_status, [k]: st || undefined } } })); };

  const validateBio = () => {
    const hb = f.health_bio; const fs = hb.field_status || {}; const errs = {};
    CLINICAL.forEach((k) => { if (!(hb[k] || "").trim() && !fs[k]) errs[k] = true; });
    ["height", "weight"].forEach((k) => { if (!(hb[k] || "").trim() && fs[k] !== "todo") errs[k] = true; });
    if (!hb.blood_type && fs.blood_type !== "todo") errs.blood_type = true;
    return errs;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.accept_terms) return toast.error(t("auth.terms_agree"));
    const errs = validateBio();
    if (Object.keys(errs).length) {
      setErrors(errs);
      const first = Object.keys(errs)[0];
      document.getElementById(`bio-field-${first}`)?.scrollIntoView({ block: "center", behavior: "smooth" });
      return toast.error(t("bio.required_hint"));
    }
    setErrors({});
    try {
      await api.post("/auth/register/user", { ...f, clinical_invite_token: ci || undefined, fiscal_invite_token: fi || undefined });
      toast.success(t("hbio.saved"));
      nav(`/confirm-email?email=${encodeURIComponent(f.email)}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };

  const hb = f.health_bio;
  return (<Layout><div className="yp-container max-w-3xl py-10">
    <h1 className="text-3xl font-bold mb-6">{t("auth.register_user_title")}</h1>
    <form onSubmit={submit} className="yp-card p-6 space-y-4" data-testid="register-user-form">
      <div className="grid md:grid-cols-2 gap-4">
        <Labeled id="ru-email" label={t("common.email")}><input id="ru-email" required className="yp-input w-full" placeholder={t("common.email")} type="email" value={f.email} onChange={e=>set("email",e.target.value)} data-testid="reg-user-email" /></Labeled>
        <Labeled id="ru-password" label={t("common.password")}><input id="ru-password" required className="yp-input w-full" placeholder={t("common.password")} type="password" minLength="8" value={f.password} onChange={e=>set("password",e.target.value)} data-testid="reg-user-password" /></Labeled>
        <Labeled id="ru-nickname" label={t("common.nickname")}><input id="ru-nickname" required className="yp-input w-full" placeholder={t("common.nickname")} value={f.nickname} onChange={e=>set("nickname",e.target.value)} data-testid="reg-user-nickname" /></Labeled>
        <Labeled id="ru-phone" label={t("auth.phone")}><input id="ru-phone" required className="yp-input w-full" placeholder={t("auth.phone")} value={f.phone} onChange={e=>set("phone",e.target.value)} data-testid="reg-user-phone" /></Labeled>
        <Labeled id="ru-first" label={t("auth.first_name")}><input id="ru-first" required className="yp-input w-full" placeholder={t("auth.first_name")} value={f.first_name} onChange={e=>set("first_name",e.target.value)} data-testid="reg-user-first" /></Labeled>
        <Labeled id="ru-last" label={t("auth.last_name")}><input id="ru-last" required className="yp-input w-full" placeholder={t("auth.last_name")} value={f.last_name} onChange={e=>set("last_name",e.target.value)} data-testid="reg-user-last" /></Labeled>
        <Labeled id="ru-bdate" label={t("auth.birth_date")}><input id="ru-bdate" required className="yp-input w-full" placeholder={t("auth.birth_date")} type="date" value={f.birth_date} onChange={e=>set("birth_date",e.target.value)} data-testid="reg-user-bdate" /></Labeled>
        <Labeled id="ru-tax" label={t("auth.tax_id")}><input id="ru-tax" required className="yp-input w-full" placeholder={t("auth.tax_id")} value={f.tax_id} onChange={e=>set("tax_id",e.target.value)} data-testid="reg-user-tax" /></Labeled>
        <Labeled id="ru-street" label={t("bio.lbl_street")}><input id="ru-street" required className="yp-input w-full" placeholder={t("bio.lbl_street")} value={f.address_street} onChange={e=>set("address_street",e.target.value)} data-testid="reg-user-street" /></Labeled>
        <Labeled id="ru-city" label={t("bio.lbl_city")}><input id="ru-city" required className="yp-input w-full" placeholder={t("bio.lbl_city")} value={f.address_city} onChange={e=>set("address_city",e.target.value)} data-testid="reg-user-city" /></Labeled>
        <Labeled id="ru-country" label={t("bio.lbl_country")}>
          <select id="ru-country" className="yp-input w-full" value={f.country} onChange={e=>set("country",e.target.value)} data-testid="reg-user-country">
            {["IT","DE","FR","AT","CH","US","UK"].map(c=><option key={c}>{c}</option>)}
          </select>
        </Labeled>
        <Labeled id="ru-lang" label={t("bio.lbl_language")}>
          <select id="ru-lang" className="yp-input w-full" value={f.language} onChange={e=>set("language",e.target.value)} data-testid="reg-user-lang">
            <option value="it">Italiano</option><option value="en">English</option><option value="de">Deutsch</option><option value="fr">Français</option>
          </select>
        </Labeled>
      </div>
      <hr className="border-slate-200 my-3" />
      <h3 className="font-semibold">{t("bio.title")}</h3>
      <p className="text-xs text-slate-500">{t("bio.subtitle")}</p>
      <div className="grid md:grid-cols-2 gap-4">
        {CLINICAL.map((k) => (
          <MedicalField key={k} fieldKey={k} multiline value={hb[k]} status={hb.field_status?.[k]}
            onValue={(v)=>setVal(k,v)} onStatus={(s)=>setStat(k,s)} options={["none","todo"]} error={errors[k]} />
        ))}
        <MedicalField fieldKey="height" value={hb.height} status={hb.field_status?.height}
          onValue={(v)=>setVal("height",v)} onStatus={(s)=>setStat("height",s)} options={["todo"]} error={errors.height} />
        <MedicalField fieldKey="weight" value={hb.weight} status={hb.field_status?.weight}
          onValue={(v)=>setVal("weight",v)} onStatus={(s)=>setStat("weight",s)} options={["todo"]} error={errors.weight} />
        <div className="md:col-span-2">
          <BloodSelect value={hb.blood_type} status={hb.field_status?.blood_type} error={errors.blood_type}
            onChange={(v)=> v === "__todo__" ? setStat("blood_type","todo") : setVal("blood_type", v)} />
        </div>
        <div className="md:col-span-2 space-y-1">
          <label htmlFor="bio-field-notes" className="block text-sm font-medium">{t("bio.notes")}</label>
          <textarea id="bio-field-notes" className="yp-input w-full" placeholder={t("bio.notes")} rows={2} value={hb.notes} onChange={e=>setVal("notes",e.target.value)} data-testid="bio-notes" />
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" className="h-5 w-5 shrink-0" checked={f.accept_terms} onChange={e=>set("accept_terms",e.target.checked)} data-testid="reg-user-terms" />
        {t("auth.terms_agree")} — <a href="/legal/terms" target="_blank" className="yp-link">{t("footer.terms")}</a> · <a href="/legal/privacy" target="_blank" className="yp-link">{t("footer.privacy")}</a>
      </label>
      <button className="yp-btn yp-btn-primary w-full" type="submit" data-testid="reg-user-submit">{t("nav.register")}</button>
    </form>
  </div></Layout>);
}
